import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RotateCw } from 'lucide-react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { useReorder, type ReorderSummary } from '@/hooks/useReorder';

/**
 * ReorderButton — rebuilds a past order into the cart in one tap, then
 * navigates to /cart. Shared by Order history rows and the order
 * confirmation page so the feedback + navigation stay identical.
 *
 * Feedback: the app has no toast library, so success/skip messaging is
 * surfaced by the caller via `onResult` (each host renders its own inline
 * notice). The button itself only owns the in-flight spinner + disabled
 * state. On success it navigates to /cart (append, never wipe — see
 * useReorder).
 */
export function ReorderButton({
  orderId,
  label = 'Reorder',
  onResult,
  navigateOnSuccess = true,
  variant = 'secondary',
  size = 'sm',
  className,
  stopPropagation = false,
}: {
  orderId: string;
  label?: string;
  onResult?: (summary: ReorderSummary | null) => void;
  navigateOnSuccess?: boolean;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  /** When the button lives inside a clickable row/link, swallow the click. */
  stopPropagation?: boolean;
}) {
  const navigate = useNavigate();
  const { reorderById } = useReorder();
  const [busy, setBusy] = useState(false);

  async function handleClick(e: React.MouseEvent) {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (busy) return;
    setBusy(true);
    try {
      const summary = await reorderById(orderId);
      onResult?.(summary);
      // Only route to the cart when something actually landed in it.
      if (navigateOnSuccess && summary && summary.added > 0) {
        navigate('/cart');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      disabled={busy}
      onClick={handleClick}
    >
      {busy ? (
        <Loader2 size={13} className="animate-spin" />
      ) : (
        <RotateCw size={13} />
      )}
      {label}
    </Button>
  );
}

/** Build a customer-facing message from a reorder summary. */
export function reorderMessage(summary: ReorderSummary | null): string {
  if (!summary) return "We couldn't load that order's items. Please try again.";
  if (summary.added === 0 && summary.skipped > 0) {
    return `None of those items are available to reorder right now (${summary.skipped} unavailable).`;
  }
  if (summary.added === 0) {
    return 'There was nothing to reorder from that order.';
  }
  const itemWord = summary.added === 1 ? 'item' : 'items';
  let msg = `Added ${summary.added} ${itemWord} to your cart`;
  if (summary.skipped > 0) {
    msg += ` (${summary.skipped} unavailable)`;
  }
  return `${msg}.`;
}
